import { useMemo, useRef, useState, type FormEvent } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, Loader2, Video, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useServerText } from "@/i18n";
import { api } from "@/lib/api";
import { Button, Card } from "@/components/ui";
import { cn } from "@/lib/utils";
import { useDialogKeys } from "@/lib/useDialogKeys";

/**
 * Send a notetaker into a meeting.
 *
 * Two modes, because they fail differently: joining now works for a call that
 * is already running, and scheduling is for one that has not started. Recall
 * needs about ten minutes of lead time on a scheduled join — it provisions
 * capacity ahead of the call — so a time closer than that is refused here
 * rather than producing a bot that quietly joins late.
 */

type Mode = "now" | "scheduled";

function detectPlatform(url: string): string | null {
  try {
    const host = new URL(url).hostname;
    if (host.includes("zoom")) return "Zoom";
    if (host.includes("meet.google")) return "Google Meet";
    if (host.includes("teams")) return "Teams";
    return null;
  } catch {
    return null;
  }
}

/** `datetime-local` wants local wall-clock, not UTC. */
function toLocalInputValue(date: Date): string {
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

export function SendBotDialog({
  projectId,
  onClose,
}: {
  projectId: string | null;
  onClose: () => void;
}) {
  const { t } = useTranslation(["library", "common"]);
  const st = useServerText();
  const queryClient = useQueryClient();
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [mode, setMode] = useState<Mode>("now");
  const [when, setWhen] = useState(() =>
    // Default 15 minutes out: past Recall's lead time, and a round-ish number
    // someone can adjust rather than compute.
    toLocalInputValue(new Date(Date.now() + 15 * 60_000)),
  );

  const platform = useMemo(() => detectPlatform(url), [url]);

  const dialogRef = useRef<HTMLDivElement>(null);

  useDialogKeys(dialogRef, onClose);

  const send = useMutation({
    mutationFn: () =>
      api.createMeeting({
        meetingUrl: url.trim(),
        title: title.trim() || undefined,
        projectId,
        joinAt: mode === "scheduled" ? new Date(when).toISOString() : undefined,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["recordings"] });
      onClose();
    },
  });

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!url.trim()) return;
    send.mutate();
  }

  const scheduledTooSoon =
    mode === "scheduled" && new Date(when).getTime() - Date.now() < 10 * 60_000;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-5 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <Card
        ref={dialogRef}
        className="max-h-[85vh] w-full max-w-[440px] overflow-y-auto p-5"
        role="dialog"
        aria-modal="true"
        // Named, so a screen reader announces what opened rather than "dialog".
        aria-labelledby="send-bot-title"
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 id="send-bot-title" className="text-[15px] font-semibold text-text">
              {t("sendBot.title")}
            </h2>
            <p className="mt-1 text-[13px] leading-relaxed text-text-muted">
              {t("sendBot.intro")}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label={t("common:actions.close")}>
            <X size={15} />
          </Button>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <label className="block">
            <span className="mb-1.5 block text-[13px] font-medium text-text">
              {t("sendBot.meetingLink")}
            </span>
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder={t("sendBot.urlPlaceholder")}
              autoFocus
              className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm text-text placeholder:text-text-faint focus:border-accent focus:outline-none"
            />
            {url.trim() ? (
              <span
                className={cn(
                  "mt-1.5 block text-[12px]",
                  platform ? "text-success" : "text-warning",
                )}
              >
                {platform
                  ? t("sendBot.recognised", { platform })
                  : t("sendBot.unsupported")}
              </span>
            ) : null}
          </label>

          <label className="block">
            <span className="mb-1.5 block text-[13px] font-medium text-text">
              {t("sendBot.titleLabel")}{" "}
              <span className="font-normal text-text-faint">{t("sendBot.optional")}</span>
            </span>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t("sendBot.titlePlaceholder")}
              className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm text-text placeholder:text-text-faint focus:border-accent focus:outline-none"
            />
          </label>

          <div>
            <span className="mb-1.5 block text-[13px] font-medium text-text">
              {t("sendBot.when")}
            </span>
            <div className="flex gap-2">
              <ModeButton
                active={mode === "now"}
                onClick={() => setMode("now")}
                icon={<Video size={14} />}
                label={t("sendBot.joinNow")}
                hint={t("sendBot.joinNowHint")}
              />
              <ModeButton
                active={mode === "scheduled"}
                onClick={() => setMode("scheduled")}
                icon={<CalendarClock size={14} />}
                label={t("sendBot.schedule")}
                hint={t("sendBot.scheduleHint")}
              />
            </div>
          </div>

          {mode === "scheduled" ? (
            <label className="block">
              <input
                type="datetime-local"
                value={when}
                onChange={(e) => setWhen(e.target.value)}
                className="h-10 w-full rounded-lg border border-border bg-bg px-3 text-sm text-text focus:border-accent focus:outline-none"
              />
              <span
                className={cn(
                  "mt-1.5 block text-[12px]",
                  scheduledTooSoon ? "text-warning" : "text-text-faint",
                )}
              >
                {scheduledTooSoon
                  ? t("sendBot.tooSoon")
                  : t("sendBot.joinsEarly")}
              </span>
            </label>
          ) : null}

          {send.error ? (
            <p
              role="alert"
              className="rounded-lg bg-danger-subtle px-3 py-2 text-[13px] text-danger"
            >
              {send.error instanceof Error
                ? st(send.error.message)
                : t("sendBot.failed")}
            </p>
          ) : null}

          <div className="flex gap-2 pt-1">
            <Button
              type="submit"
              variant="primary"
              disabled={!url.trim() || send.isPending || scheduledTooSoon}
              className="flex-1"
            >
              {send.isPending ? <Loader2 size={15} className="animate-spin" /> : null}
              {mode === "now" ? t("sendBot.submitNow") : t("sendBot.submitScheduled")}
            </Button>
            <Button variant="secondary" onClick={onClose}>
              {t("common:actions.cancel")}
            </Button>
          </div>

          <p className="text-[12px] leading-relaxed text-text-faint">
            {t("sendBot.consent")}
          </p>
        </form>
      </Card>
    </div>
  );
}

function ModeButton({
  active,
  onClick,
  icon,
  label,
  hint,
}: {
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  hint: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "flex-1 rounded-lg border px-3 py-2.5 text-left transition-colors",
        active ? "border-accent bg-accent-subtle" : "border-border hover:bg-surface-hover",
      )}
    >
      <span className="flex items-center gap-1.5 text-[13px] font-medium text-text">
        {icon}
        {label}
      </span>
      <span className="mt-0.5 block text-[11.5px] text-text-muted">{hint}</span>
    </button>
  );
}
